/**
 * Phase 10e — Formulaire de création manuelle d'une facture fournisseur.
 *
 * Modal (Dialog shadcn) contenant :
 *   - Zone drag & drop PDF (max 10 Mo, magic bytes vérifiés côté backend)
 *   - Champs fournisseur (nom, TVA, IBAN)
 *   - Champs facture (N°, dates, communication paiement)
 *   - Éditeur lignes TVA (HTVA + taux 6/21/0%) avec calcul auto
 *   - Chantier rattaché (dropdown) OU purchase_order_ref texte
 *   - Notes internes
 *
 * Contrat backend :
 *   POST /api/supplier-invoices/manual  (multipart)
 *     file    : PDF blob
 *     payload : JSON stringifié
 *
 *   Succès → 201 avec la facture créée
 *   409    → conflict payload (dédup) → parent gère l'affichage DuplicateInvoiceModal
 *   4xx    → toast avec message d'erreur
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Trash2, Loader2, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MAX_MB = 10;
const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const VAT_RATES = [21, 6, 0];

function emptyLine() {
  return { description: "", quantity: 1, unit_price: 0, total_htva: 0, vat_rate: 21 };
}

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function SupplierInvoiceManualForm({ open, onOpenChange, onCreated, onDuplicate }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [chantiers, setChantiers] = useState([]);
  const [form, setForm] = useState({
    supplier_name: "",
    supplier_vat: "",
    supplier_iban: "",
    invoice_number: "",
    issue_date: todayISO(),
    due_date: "",
    purchase_order_ref: "",
    payment_reference: "",
    linked_chantier_id: "",
    internal_notes: "",
    lines: [emptyLine()],
  });

  // Charge chantiers actifs pour le dropdown
  useEffect(() => {
    if (!open) return;
    api
      .get("/chantiers")
      .then(({ data }) => setChantiers((data || []).filter((c) => c.status !== "termine" && c.status !== "finalise")))
      .catch(() => setChantiers([]));
  }, [open]);

  // Reset form à l'ouverture
  useEffect(() => {
    if (open) {
      setFile(null);
      setForm({
        supplier_name: "",
        supplier_vat: "",
        supplier_iban: "",
        invoice_number: "",
        issue_date: todayISO(),
        due_date: "",
        purchase_order_ref: "",
        payment_reference: "",
        linked_chantier_id: "",
        internal_notes: "",
        lines: [emptyLine()],
      });
    }
  }, [open]);

  // Calcul auto totaux à partir des lignes
  const totals = useMemo(() => {
    const breakdown = {};
    let htva = 0;
    let vat = 0;
    for (const l of form.lines) {
      const h = Number(l.total_htva) || 0;
      const rate = Number(l.vat_rate) || 0;
      const v = h * (rate / 100);
      htva += h;
      vat += v;
      if (!breakdown[rate]) breakdown[rate] = { rate, base_htva: 0, vat_amount: 0 };
      breakdown[rate].base_htva += h;
      breakdown[rate].vat_amount += v;
    }
    return {
      htva: Math.round(htva * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      tvac: Math.round((htva + vat) * 100) / 100,
      breakdown: Object.values(breakdown).map((b) => ({
        rate: b.rate,
        base_htva: Math.round(b.base_htva * 100) / 100,
        vat_amount: Math.round(b.vat_amount * 100) / 100,
      })),
    };
  }, [form.lines]);

  const onFileChange = (f) => {
    if (!f) return setFile(null);
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_MB} Mo).`);
      return;
    }
    setFile(f);
  };

  const setLine = (idx, patch) => {
    const next = [...form.lines];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, lines: next });
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const removeLine = (idx) =>
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });

  const canSubmit =
    file &&
    form.supplier_name.trim() &&
    form.invoice_number.trim() &&
    form.issue_date &&
    form.lines.length > 0 &&
    form.lines.some((l) => Number(l.total_htva) > 0);

  const onSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        supplier_name: form.supplier_name.trim(),
        supplier_vat: form.supplier_vat.trim() || null,
        supplier_iban: form.supplier_iban.trim() || null,
        invoice_number: form.invoice_number.trim(),
        issue_date: form.issue_date,
        due_date: form.due_date || null,
        amount_htva: totals.htva,
        amount_vat: totals.vat,
        amount_tvac: totals.tvac,
        currency: "EUR",
        vat_breakdown: totals.breakdown,
        line_items: form.lines.map((l) => ({
          description: l.description || "",
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          total_htva: Number(l.total_htva) || 0,
          vat_rate: Number(l.vat_rate) || 21,
        })),
        purchase_order_ref: form.purchase_order_ref.trim() || null,
        payment_reference: form.payment_reference.trim() || null,
        linked_chantier_id: form.linked_chantier_id || null,
        internal_notes: form.internal_notes.trim() || null,
      };

      const fd = new FormData();
      fd.append("file", file);
      fd.append("payload", JSON.stringify(payload));

      const { data } = await api.post("/supplier-invoices/manual", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Facture manuelle créée.");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409 && e?.response?.data?.detail && typeof e.response.data.detail === "object") {
        // Dédup — parent gère l'affichage du modal
        onOpenChange(false);
        onDuplicate?.(e.response.data.detail);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto border-slate-800 bg-slate-900 text-slate-100"
        data-testid="supplier-invoice-manual-form"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#C4553B]" />
            Nouvelle facture fournisseur (upload manuel)
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            PDF obligatoire ({MAX_MB} Mo max). Saisissez les métadonnées ci-dessous.
            Un doublon (même fournisseur + n° facture) sera bloqué.
          </DialogDescription>
        </DialogHeader>

        {/* --- Zone PDF --- */}
        <div className="rounded border border-dashed border-slate-700 bg-slate-950/40 p-4">
          {file ? (
            <div className="flex items-center justify-between gap-3" data-testid="pdf-selected-block">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 shrink-0 text-emerald-400" />
                <div className="min-w-0">
                  <div className="truncate text-sm text-slate-100" data-testid="pdf-filename">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} Ko</div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFile(null)}
                className="border-slate-700 bg-slate-800 text-slate-200"
                data-testid="pdf-clear-btn"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Retirer
              </Button>
            </div>
          ) : (
            <label
              className="flex cursor-pointer flex-col items-center justify-center gap-2 py-6 text-center"
              data-testid="pdf-drop-zone"
            >
              <Upload className="h-6 w-6 text-slate-500" />
              <div className="text-sm text-slate-300">
                <span className="font-medium">Cliquez pour sélectionner un PDF</span>
                {" "}(max {MAX_MB} Mo)
              </div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => onFileChange(e.target.files?.[0])}
                className="hidden"
                data-testid="pdf-input"
              />
            </label>
          )}
        </div>

        {/* --- Fournisseur --- */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="sup-name" className="text-slate-300 text-xs uppercase tracking-wide">Fournisseur *</Label>
            <Input
              id="sup-name"
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              placeholder="Ex : Etablissements Dupont SPRL"
              className="border-slate-700 bg-slate-950/40"
              data-testid="input-supplier-name"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">N° TVA (optionnel)</Label>
            <Input
              value={form.supplier_vat}
              onChange={(e) => setForm({ ...form, supplier_vat: e.target.value.toUpperCase() })}
              placeholder="BE0123456789"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="input-supplier-vat"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">IBAN (optionnel)</Label>
            <Input
              value={form.supplier_iban}
              onChange={(e) => setForm({ ...form, supplier_iban: e.target.value.toUpperCase() })}
              placeholder="BE68 5390 0754 7034"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="input-supplier-iban"
            />
          </div>
        </div>

        {/* --- Facture --- */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">N° facture *</Label>
            <Input
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              placeholder="FAC-2026-0042"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="input-invoice-number"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Date facture *</Label>
            <Input
              type="date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              className="border-slate-700 bg-slate-950/40"
              data-testid="input-issue-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Échéance</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="border-slate-700 bg-slate-950/40"
              data-testid="input-due-date"
            />
          </div>
        </div>

        {/* --- Références et rattachement --- */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Bon de commande / Réf chantier</Label>
            <Input
              value={form.purchase_order_ref}
              onChange={(e) => setForm({ ...form, purchase_order_ref: e.target.value })}
              placeholder="CHT-2026-042 ou libre"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="input-po-ref"
            />
            <p className="text-xs text-slate-500">Facilite la proposition auto-link chantier.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Chantier rattaché (optionnel)</Label>
            <Select
              value={form.linked_chantier_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, linked_chantier_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="select-linked-chantier"
              >
                <SelectValue placeholder="— Aucun —" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="__none__">— Aucun —</SelectItem>
                {chantiers.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`chantier-opt-${c.id}`}>
                    {c.name}
                    {c.reference_code && <span className="ml-2 text-xs font-mono text-slate-500">{c.reference_code}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">Communication paiement (optionnelle)</Label>
            <Input
              value={form.payment_reference}
              onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
              placeholder="+++123/4567/89012+++ ou libre"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="input-payment-ref"
            />
          </div>
        </div>

        {/* --- Lignes TVA --- */}
        <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide text-slate-500">Lignes TVA</div>
            <Button
              variant="outline"
              size="sm"
              onClick={addLine}
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="add-line-btn"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Ligne
            </Button>
          </div>
          <div className="space-y-2">
            {form.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`line-row-${i}`}>
                <Input
                  className="col-span-12 border-slate-700 bg-slate-950/60 sm:col-span-5"
                  placeholder="Description"
                  value={l.description}
                  onChange={(e) => setLine(i, { description: e.target.value })}
                  data-testid={`line-desc-${i}`}
                />
                <Input
                  className="col-span-6 border-slate-700 bg-slate-950/60 text-right sm:col-span-3"
                  type="number"
                  step="0.01"
                  placeholder="HTVA €"
                  value={l.total_htva}
                  onChange={(e) => setLine(i, { total_htva: e.target.value })}
                  data-testid={`line-htva-${i}`}
                />
                <Select
                  value={String(l.vat_rate)}
                  onValueChange={(v) => setLine(i, { vat_rate: parseFloat(v) })}
                >
                  <SelectTrigger
                    className="col-span-4 border-slate-700 bg-slate-950/60 sm:col-span-3"
                    data-testid={`line-vat-${i}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    {VAT_RATES.map((r) => (
                      <SelectItem key={r} value={String(r)}>{r} %</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  onClick={() => removeLine(i)}
                  disabled={form.lines.length <= 1}
                  className="col-span-2 rounded border border-slate-800 bg-slate-950/40 text-slate-400 hover:text-red-300 disabled:opacity-30 sm:col-span-1"
                  data-testid={`line-remove-${i}`}
                  aria-label="Supprimer la ligne"
                >
                  <Trash2 className="mx-auto h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          {/* Totaux calculés */}
          <div className="mt-3 grid grid-cols-3 gap-3 border-t border-slate-800 pt-3 text-sm">
            <div>
              <div className="text-xs text-slate-500">HTVA</div>
              <div className="tabular-nums" data-testid="totals-htva">{money(totals.htva)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">TVA</div>
              <div className="tabular-nums" data-testid="totals-vat">{money(totals.vat)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">TVAC</div>
              <div className="font-semibold text-base tabular-nums" data-testid="totals-tvac">{money(totals.tvac)}</div>
            </div>
          </div>
        </div>

        {/* --- Notes internes --- */}
        <div className="space-y-1">
          <Label className="text-slate-300 text-xs uppercase tracking-wide">Notes internes (optionnel)</Label>
          <Textarea
            value={form.internal_notes}
            onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
            placeholder="Contexte, référence dossier, etc. Non visible côté fournisseur."
            className="min-h-[80px] border-slate-700 bg-slate-950/40"
            data-testid="input-internal-notes"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-slate-700 bg-slate-800 text-slate-200"
            data-testid="manual-form-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit || submitting}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="manual-form-submit"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Créer la facture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
